import { useTranslation } from 'react-i18next';

import { Takeoff } from '../../../../common/hooks/use-saved-rooms-api.hook';
import { formatNumber, NUMERIC } from '../../../../components/design-system/admin';
import { cn } from '@/lib/utils';

const money = (amount: number, currency: string, lang: string) => {
    try {
        return new Intl.NumberFormat(lang, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
    } catch {
        return `${Math.round(amount)} ${currency}`;
    }
};

/**
 * A project's or a room's quantities, as the API worked them out: area, spare,
 * boxes, and the money where the shop gave a price. "About", with a range,
 * whenever a photograph measured the area; never a zero for a missing price.
 */
export function TakeoffList({ takeoff, testId }: { takeoff: Takeoff; testId?: string }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    if (!takeoff.rows.length) return <p className="text-sm text-muted-foreground">{t('SAVED_ROOMS.TAKEOFF.EMPTY')}</p>;

    return (
        <div data-testid={testId}>
            <div className="divide-y rounded-lg border">
                {takeoff.rows.map((row) => {
                    const approx = !!row.confidence && row.confidence !== 'confirmed';
                    const rooms = Array.from(new Set(row.rooms.map((r) => r.roomName))).join(', ');
                    return (
                        <div key={row.productUuid} className="flex items-start gap-3 p-3">
                            {row.thumbnail ? (
                                <img src={row.thumbnail} alt="" className="size-10 shrink-0 rounded-md border object-cover" />
                            ) : (
                                <span className="size-10 shrink-0 rounded-md border bg-muted" />
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{row.name}</p>
                                <p className="truncate text-xs text-muted-foreground">{rooms}</p>
                                <p className={cn('mt-1 text-xs', NUMERIC)}>
                                    {row.areaM2 != null
                                        ? `${approx ? t('SAVED_ROOMS.ABOUT', { value: `${formatNumber(row.areaM2, 1)} m²` }) : `${formatNumber(row.areaM2, 1)} m²`} · ${t('SAVED_ROOMS.TAKEOFF.SPARE', { pct: row.wastePct })}`
                                        : t('SAVED_ROOMS.TAKEOFF.NOT_MEASURED')}
                                    {row.boxes != null && ` · ${t('SAVED_ROOMS.TAKEOFF.BOXES', { count: row.boxes })}`}
                                </p>
                            </div>
                            <div className={cn('shrink-0 text-end', NUMERIC)}>
                                {row.total != null && row.currency ? (
                                    <>
                                        <p className="text-sm font-medium">{approx ? t('SAVED_ROOMS.ABOUT', { value: money(row.total, row.currency, lang) }) : money(row.total, row.currency, lang)}</p>
                                        {row.unitPrice && <p className="text-xs text-muted-foreground">{money(row.unitPrice.amount, row.unitPrice.currency, lang)} / {t(`SAVED_ROOMS.UNIT.${row.unitPrice.unit.toUpperCase()}`, row.unitPrice.unit)}</p>}
                                    </>
                                ) : (
                                    <p className="text-xs text-muted-foreground">{t('SAVED_ROOMS.TAKEOFF.NO_PRICE')}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {takeoff.totals.map((tot) => (
                <div key={tot.currency} className="mt-3 flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium">{t('SAVED_ROOMS.TAKEOFF.TOTAL')}</span>
                    <span className={cn('text-end', NUMERIC)}>
                        <span className="block text-base font-medium">
                            {takeoff.approximate ? t('SAVED_ROOMS.ABOUT', { value: money(tot.total, tot.currency, lang) }) : money(tot.total, tot.currency, lang)}
                        </span>
                        {takeoff.approximate && (
                            <span className="block text-xs text-muted-foreground">
                                {money(tot.range[0], tot.currency, lang)} – {money(tot.range[1], tot.currency, lang)}
                            </span>
                        )}
                    </span>
                </div>
            ))}
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                {takeoff.approximate && <p>{t('SAVED_ROOMS.TAKEOFF.APPROX')}</p>}
                {takeoff.unpriced > 0 && <p>{t('SAVED_ROOMS.TAKEOFF.UNPRICED', { count: takeoff.unpriced })}</p>}
            </div>
        </div>
    );
}
